import { createSlice } from '@reduxjs/toolkit'
import { getSeeData, getStreamKeyFromMonitoringProcessedPayload } from '@/views/commandCenter/profile/ServiceAlerts/monitoringSsePayloadUtils'
import {
  addMonitoringAlarmProcessed,
  addSseMessage,
  clearMonitoringAlarmBaseline,
  clearSseMessages,
  unwrapDepartureSsePayload
} from './sseSlice'
import {
  applyDepartureSseEvent,
  applyMonitoringAlarmMessage,
  buildDepartureByStreamKeyFromDevices,
  listAskRegisterStreamKeys,
  mergeDepartureWithReturnAwaiting,
  rebuildScenesFromDevices,
  seedScenesFromAlarmList
} from './surveillanceMessage'

const emptyData = () => ({
  /** 最近一次灌入的设备行，断线清 SSE 场景后用它重新铺底 */
  devices: [],
  sceneByStreamKey: {},
  /** 该路场景来自 SSE，列表刷新不再用 list 覆盖 */
  sseSceneKeys: {},
  departureByStreamKey: {},
  /** SSE 已推 in_place：挡住 list 里尚未刷新的旧离室单 */
  inPlaceKeys: {},
  /** 已合并进格子的离室 SSE seq，避免列表刷新时再套一次 */
  appliedDepartureSeq: {},
  processedKeys: {}
})

const defaultState = {
  fetching: false,
  error: null,
  query: {},
  data: emptyData()
}

function isDepartureEventType (type) {
  return type === 'departure_status'
    || type === 'departure_return'
    || type === 'departure_timeout_warning'
}

const surveillanceMessageSlice = createSlice({
  name: 'surveillanceMessage',
  initialState: defaultState,
  reducers: {
    resetSurveillanceMessages: () => ({
      fetching: false,
      error: null,
      query: {},
      data: emptyData()
    }),
    applySurveillanceDeviceList: (state, action) => {
      const payload = action.payload || {}
      const devices = Array.isArray(payload.devices) ? payload.devices : []
      state.data.devices = devices
      const fromDevices = buildDepartureByStreamKeyFromDevices(devices)
      Object.keys(fromDevices).forEach((streamKey) => {
        delete state.data.inPlaceKeys[streamKey]
      })
      Object.keys(state.data.inPlaceKeys).forEach((streamKey) => {
        if (!fromDevices[streamKey]) delete state.data.inPlaceKeys[streamKey]
      })
      const departureByStreamKey = mergeDepartureWithReturnAwaiting(
        fromDevices,
        state.data.departureByStreamKey,
        payload.awaitingSaved,
        state.data.inPlaceKeys
      )
      listAskRegisterStreamKeys(devices).forEach((streamKey) => {
        delete departureByStreamKey[streamKey]
      })
      state.data.departureByStreamKey = departureByStreamKey
      seedScenesFromAlarmList(
        state.data,
        payload.monitoringAlarmSseList,
        payload.monitoringAlarmProcessedKeys
      )
    },
    upsertSurveillanceDeparture: (state, action) => {
      const { streamKey, row } = action.payload || {}
      if (!streamKey || !row) return
      state.data.departureByStreamKey[streamKey] = row
    },
    removeSurveillanceDeparture: (state, action) => {
      const streamKey = action.payload && action.payload.streamKey
      if (!streamKey) return
      delete state.data.departureByStreamKey[streamKey]
    },
    applySurveillanceReturnPending: (state, action) => {
      const { streamKey, seed } = action.payload || {}
      if (!streamKey) return
      const existing = state.data.departureByStreamKey[streamKey] || seed
      if (!existing) return
      if (existing.status === 'return_pending') return
      if (
        existing.status
        && existing.status !== 'active'
        && existing.status !== 'return_awaiting'
        && existing.status !== 'pre_departed'
      ) {
        return
      }
      state.data.departureByStreamKey[streamKey] = {
        ...existing,
        status: 'return_pending',
        returnAt: Date.now()
      }
    },
    markSurveillanceReturnAwaiting: (state, action) => {
      const streamKey = action.payload && action.payload.streamKey
      if (!streamKey) return
      const existing = state.data.departureByStreamKey[streamKey]
      if (!existing) return
      state.data.departureByStreamKey[streamKey] = {
        ...existing,
        status: 'return_awaiting',
        priorStatus: existing.status,
        returnAt: Date.now(),
        autoReturn: false
      }
    },
    revertSurveillanceReturnAwaiting: (state, action) => {
      const streamKey = action.payload && action.payload.streamKey
      if (!streamKey) return
      const cur = state.data.departureByStreamKey[streamKey]
      if (!cur || cur.status !== 'return_awaiting') return
      const priorStatus = cur.priorStatus === 'active' || cur.priorStatus === 'pre_departed'
        ? cur.priorStatus
        : 'active'
      state.data.departureByStreamKey[streamKey] = {
        id: cur.id,
        type: cur.type,
        ...(cur.subType != null ? { subType: cur.subType } : {}),
        nurseId: cur.nurseId,
        nurseName: cur.nurseName,
        nurseAvatar: cur.nurseAvatar,
        departedAt: cur.departedAt,
        status: priorStatus
      }
    },
    syncSurveillanceDepartureSse: (state, action) => {
      const map = action.payload && action.payload.departureSseByStreamKey
      if (!map || typeof map !== 'object') return
      Object.keys(map).forEach((streamKey) => {
        const evt = map[streamKey]
        if (!evt) return
        if (state.data.appliedDepartureSeq[streamKey] === evt.seq) return
        state.data.appliedDepartureSeq[streamKey] = evt.seq
        applyDepartureSseEvent(state.data, streamKey, evt)
      })
    }
  },
  extraReducers: (builder) => {
    const dropSseScenes = (state) => {
      state.data.sseSceneKeys = {}
      state.data.processedKeys = {}
      state.data.sceneByStreamKey = {}
      rebuildScenesFromDevices(state.data)
    }

    builder
      .addCase(addSseMessage, (state, action) => {
        const msg = action.payload || {}
        const type = msg.type || 'message'
        if (type === 'monitoring_alarm') {
          applyMonitoringAlarmMessage(state.data, msg)
          return
        }
        if (type === 'message') {
          const see = getSeeData(msg)
          if (see && typeof see === 'object' && (
            see.hasBaby !== undefined || see.isViolation !== undefined
            || see.messageBO || see.messageBody || see.messageMap
          )) {
            applyMonitoringAlarmMessage(state.data, msg)
          }
          return
        }
        if (!isDepartureEventType(type)) return
        const payload = unwrapDepartureSsePayload(msg.data) || {}
        const streamKey = getStreamKeyFromMonitoringProcessedPayload(payload)
        if (!streamKey) return
        applyDepartureSseEvent(state.data, streamKey, {
          eventType: type,
          payload
        })
      })
      .addCase(addMonitoringAlarmProcessed, (state, action) => {
        const sk = getStreamKeyFromMonitoringProcessedPayload(action.payload)
        if (!sk) return
        state.data.processedKeys[sk] = Date.now()
        const entry = state.data.sceneByStreamKey[sk]
        if (!entry) return
        state.data.sceneByStreamKey[sk] = { ...entry, ticketClosed: true }
      })
      .addCase(clearMonitoringAlarmBaseline, dropSseScenes)
      .addCase(clearSseMessages, dropSseScenes)
  }
})

export const {
  resetSurveillanceMessages,
  applySurveillanceDeviceList,
  upsertSurveillanceDeparture,
  removeSurveillanceDeparture,
  applySurveillanceReturnPending,
  markSurveillanceReturnAwaiting,
  revertSurveillanceReturnAwaiting,
  syncSurveillanceDepartureSse
} = surveillanceMessageSlice.actions

export default surveillanceMessageSlice.reducer
